import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, CalendarBlank } from "@phosphor-icons/react";
import { humanizeAxiosError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

export default function MonthlyLeaves() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [name, setName] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // İK/Admin/GM ay ve yıl serbestçe seçebilir.
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const q = `/reports/monthly-leaves?year=${year}&month=${month}${name.trim() ? `&name=${encodeURIComponent(name.trim())}` : ""}`;
      const r = await api.get(q);
      setData(r.data);
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setLoading(false); }
  }, [year, month, name]);
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [load]);

  const downloadXlsx = async () => {
    try {
      const nameParam = name.trim() ? `&name=${encodeURIComponent(name.trim())}` : "";
      const r = await fetch(`${API}/api/reports/monthly-leaves?year=${year}&month=${month}${nameParam}&fmt=xlsx`, { credentials: "include" });
      if (!r.ok) throw new Error("İndirme başarısız");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `izinler_${year}_${month ? String(month).padStart(2, "0") : "yil"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel indirildi");
    } catch (e) { toast.error(e.message || "İndirme başarısız"); }
  };

  const years = [];
  for (let y = now.getFullYear() + 1; y >= 2020; y--) years.push(y);

  return (
    <div className="space-y-6" data-testid="monthly-leaves-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Rapor</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-2">
            <CalendarBlank size={28} weight="duotone" className="text-primary" />
            Aylık İzin Raporu
          </h1>
          <div className="text-sm text-muted-foreground mt-1">Departman bazlı alfabetik sıralı — İK/Admin/GM için.</div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs">Personel Ara</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="İsme göre filtrele…"
              className="w-48"
              data-testid="name-filter-input"
            />
          </div>
          <div>
            <Label className="text-xs">Yıl</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28" data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Ay</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-36" data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Tüm Yıl</SelectItem>
                {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={downloadXlsx} data-testid="download-xlsx"><Download size={16} className="mr-1" />Excel İndir</Button>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">Yükleniyor…</CardContent></Card>
      ) : !data || data.total === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground text-sm">
          {name.trim() ? `"${name.trim()}" için ` : ""}{month ? `${MONTHS[month - 1]} ${year}` : `${year} yılı`} {month ? "ayında" : ""} izin kaydı bulunmuyor.
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge>{data.total} izin</Badge>
            <Badge variant="secondary">{data.departments.length} departman</Badge>
          </div>
          {data.departments.map((d) => (
            <Card key={d.name} data-testid={`dept-${d.name}`}>
              <CardContent className="p-0">
                <div className="px-6 py-3 bg-muted/50 border-b border-border">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-display font-bold text-lg">{d.name}</div>
                    <Badge variant="outline">{d.leaves.length} personel izin</Badge>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left p-3">Personel</th>
                        <th className="text-left p-3">İzin Türü</th>
                        <th className="text-left p-3">Başlangıç</th>
                        <th className="text-left p-3">İş Başı</th>
                        <th className="text-left p-3">Gün</th>
                        <th className="text-left p-3">Durum</th>
                        <th className="text-left p-3">Talep</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.leaves.map((lv) => (
                        <tr key={lv.id} className="border-t border-border">
                          <td className="p-3 font-medium">{lv.user_name}</td>
                          <td className="p-3">{lv.leave_type_label || lv.leave_type}</td>
                          <td className="p-3">{lv.start_date}</td>
                          <td className="p-3">{lv.end_date}</td>
                          <td className="p-3">{lv.day_count}</td>
                          <td className="p-3">
                            <Badge variant={lv.status === "approved" ? "default" : lv.status === "rejected" ? "destructive" : "secondary"}>{lv.status}</Badge>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground">{lv.requested_at}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
